/* eslint-disable spire/export-styles */
import { fontSizes } from "@dana/MobiusOverrides/styles/typography";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
}

export interface StyleProps {
    wrapper?: InjectableCss;
    printOnly?: InjectableCss;
}

export const productQuantityWrapperStyles: StyleProps = {
    printOnly: {
        css: css`
            display: none;
            @media print {
                display: flex;
            }
        `
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: flex-start;
            gap: 16px;
            @media print {
                flex-direction: column;
                align-items: flex-start;
                gap: 0;
                .pricing-subtext {
                    font-size: ${fontSizes.xSmall};
                }
            }
        `,
    },
};

const mapStateToProps = (
    state: ApplicationStateDana,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        currencyCode: state.context.session.currency?.currencyCode ?? "",
        canUserSeePrice: state?.showPriceToggle.canSeePrice ?? false,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const wrapperStyles = productQuantityWrapperStyles;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-inner-spin-button,
            input[type="number"]::-webkit-outer-spin-button {
                opacity: 1;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
    css: css`
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        opacity: 1;
        transform: scale(1.4);
    }
    -moz-appearance: number-input;`

};

const ProductQuantityOrdered = ({
    canUserSeePrice,
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    currencyCode,
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered?.toString());
    const showAddToCart = properties?.showRtbCodeAddToCart;
    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    return (
        <StyledWrapper {...wrapperStyles.wrapper} className="qo-list-item">
            <TextField
                label={translate("QTY")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles}
            />
            <StyledWrapper {...wrapperStyles.printOnly}>
                {translate("QTY")}: {productQty}
            </StyledWrapper>
            {labelOverride === true ? null : (
                showAddToCart === "true" && canUserSeePrice && (
                    <Typography className="text-body-md pricing-subtext">
                        {translate("Price")} ({currencyCode})
                    </Typography>
                )
            )}

        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
